/**
 * @agentteams/runner-request — Web·Portal 공용 러너 요청(트리거) 전이 커널.
 *
 * 러너 요청 모달이 양쪽 앱에서 기존 모습·동작 그대로 동작하는 데 필요한 의존 중
 * 앱(React·라우터·전역 상태·API 클라이언트)에 닿지 않는 순수 규칙만 둔다.
 * 손작성 ESM 무의존 패키지 관례를 따르므로 이 파일에는 import가 없다 —
 * web/src·portal 역방향 import는 구조상 불가능하며 index.test.mjs가 이를 고정한다.
 *
 * 원본과 행위 동일성을 유지한다. 각 절의 `원본:` 주석이 가리키는 web 파일이 SSOT이며,
 * 이 패키지로 소비자를 전환하는 작업(Task 3)에서 원본을 제거할 때까지 양쪽이 공존한다.
 *UI(모달·편집기·첨부·워크트리·정책 선택 컴포넌트)와 어댑터 계약은 이 패키지 위에 얹히며
 * 이번 추출 범위 밖이다.
 */

// ---------------------------------------------------------------------------
// 정책 선택 (원본: web/src/lib/runnerDispatchPolicySelection.ts, 그대로 이동)
// ---------------------------------------------------------------------------

export const noRunnerDispatchPolicyValue = '__none__';

export const isRunnerDispatchPolicySelected = ({ hasDispatchContext, selectedPolicyId }) =>
  hasDispatchContext && selectedPolicyId !== null && selectedPolicyId !== noRunnerDispatchPolicyValue;

/**
 * 정책 목록/추천 도착(또는 refetch) 후 적용할 자동 선택값을 계산한다.
 * null을 반환하면 현재 선택을 그대로 둔다.
 *
 * - USER 선택은 그 값이 유효한 동안(목록에 존재하거나 "사용 안 함") 절대 덮어쓰지 않는다.
 * - AUTO 상태에서는 서버 추천 정책을 우선 선택하고, 추천이 없거나 목록에 없으면 첫 정책을 쓴다.
 * - USER 선택이 더 이상 유효하지 않으면(정책 삭제 등) AUTO 규칙으로 복구한다.
 */
export const resolveAutoDispatchPolicySelection = ({
  selectionSource,
  selectedPolicyId,
  policyIds,
  recommendedPolicyId,
}) => {
  if (policyIds.length === 0) return null;

  const isSelectionValid =
    selectedPolicyId !== null &&
    (selectedPolicyId === noRunnerDispatchPolicyValue || policyIds.includes(selectedPolicyId));

  if (selectionSource === 'USER' && isSelectionValid) return null;

  const target =
    recommendedPolicyId !== null && policyIds.includes(recommendedPolicyId) ? recommendedPolicyId : policyIds[0];
  return target === selectedPolicyId ? null : target;
};

export const isRunnerDispatchPolicySelectionPending = ({
  hasDispatchContext,
  isFetching,
  policyCount,
  selectedPolicyId,
}) => hasDispatchContext && (isFetching || (policyCount > 0 && selectedPolicyId === null));

// ---------------------------------------------------------------------------
// 동시 실행 충돌 판정 (원본: web/src/lib/daemonTriggerConflict.ts)
// ---------------------------------------------------------------------------
// 원본의 `DaemonTrigger['status']` 타입 표기만 문자열 집합으로 풀었다. 런타임 로직은
// 그대로이며, 서버 SSOT(api/src/utils/runnerClaimConflict.ts의 `resolveWorkspaceKey`)와의
// 미러 관계·알려진 한계(프로젝트 범위 목록 vs 러너 단위 claim 차단)도 그대로 계승한다.

const activeDaemonTriggerStatuses = new Set(['PENDING', 'IN_PROGRESS', 'CANCEL_REQUESTED']);
const runningDaemonTriggerStatuses = new Set(['IN_PROGRESS', 'CANCEL_REQUESTED']);

// 클라이언트 캐시가 stale인 경우 백엔드의 active-only 응답에 종료된 트리거가 섞여 들어올 수 있어
// conflict 판정 및 active 요약 표시에서는 명시적으로 active 상태만 신뢰합니다.
export const isActiveDaemonTriggerStatus = (status) => activeDaemonTriggerStatuses.has(status);

/// 체크아웃 미보고 sentinel. git 브랜치가 아니라 "워크트리를 쓰지 않는 실행인데 어느 폴더인지 모른다"는
/// 뜻이며, `workspaceKeysOverlap`에서 비-워크트리 계열 전체와 겹치는 와일드카드로 동작한다(서버와 같은 규약).
const UNKNOWN_CHECKOUT_WORKSPACE_KEY = 'main';
const CHECKOUT_KEY_PREFIX = 'checkout:';

/// `null`은 "아직 어떤 디렉터리도 점유하지 않았다"를 뜻하는 전용 값이다(서버와 같은 규약).
/// 어떤 키와도 — 다른 null과도 — 겹치지 않으므로 비교 지점이 null을 먼저 걸러야 한다.
const resolveWorkspaceKey = (trigger) => {
  if (trigger.discoveredWorktreeId) {
    return `discovered:${trigger.discoveredWorktreeId}`;
  }

  if (trigger.useWorktree) {
    if (trigger.worktreeId) {
      return `worktree:${trigger.worktreeId}`;
    }

    // 워크트리 실행인데 두 id가 모두 없다 = 아직 어떤 폴더도 잡지 않았다(새로 만들 워크트리).
    return null;
  }

  return trigger.checkoutId ? `${CHECKOUT_KEY_PREFIX}${trigger.checkoutId}` : UNKNOWN_CHECKOUT_WORKSPACE_KEY;
};

const isCheckoutFamilyKey = (key) => key === UNKNOWN_CHECKOUT_WORKSPACE_KEY || key.startsWith(CHECKOUT_KEY_PREFIX);

/// 두 작업공간이 같은 폴더를 가리킬 **수 있는가**(서버 `workspaceKeysOverlap`의 미러).
/// 동등 비교가 아닌 이유: 체크아웃 미보고는 "어느 폴더인지 모른다"는 뜻이라 어떤 체크아웃과도 같을 수 있고,
/// 서버가 그 경우 직렬화하므로 화면도 같은 방향(경고)으로 접혀야 한다.
const workspaceKeysOverlap = (keyA, keyB) => {
  if (keyA === null || keyB === null) {
    return false;
  }

  if (keyA === keyB) {
    return true;
  }

  return (
    isCheckoutFamilyKey(keyA) &&
    isCheckoutFamilyKey(keyB) &&
    (keyA === UNKNOWN_CHECKOUT_WORKSPACE_KEY || keyB === UNKNOWN_CHECKOUT_WORKSPACE_KEY)
  );
};

/**
 * 서버 `evaluateActiveTriggerConflict`(api/src/routes/daemon-triggers/conflict.ts)와 동일 의미의
 * 클라이언트 미러. 판정축은 작업 디렉터리 정체성이며, 같은 폴더를 쓰는 실행이 있으면 큐잉 안내를
 * 위해 WARN을 반환한다(생성은 거절하지 않는다).
 *
 * [알려진 한계] 여기서 보는 활성 트리거 목록은 프로젝트 범위인데 서버의 claim 차단은 러너 단위로
 * 프로젝트를 가로지른다. 공유 러너가 다른 프로젝트 작업으로 바쁠 때는 경고를 띄우지 못한다.
 */
export function evaluateActiveTriggerConflict(input) {
  const { agentConfigId, daemonId, planMode, activeTriggers, checkoutIdByAgentConfigId, excludeTriggerId } = input;

  if (agentConfigId.length === 0 || daemonId.length === 0) {
    return { verdict: 'NONE', reason: null };
  }

  const resolveCheckoutId = (id) => checkoutIdByAgentConfigId?.get(id) ?? null;

  const candidateWorkspaceKey = resolveWorkspaceKey({ ...input, checkoutId: resolveCheckoutId(agentConfigId) });

  // 아직 어떤 디렉터리도 잡지 않은 요청(새로 만들 워크트리)은 무엇과도 겹치지 않는다.
  if (candidateWorkspaceKey === null) {
    return { verdict: 'NONE', reason: null };
  }

  // agentConfigId로 범위를 좁히지 않는다 — claim 차단은 러너 단위라 에이전트가 달라도 대기에 걸린다.
  const conflictingTriggers = activeTriggers.filter(
    (trigger) =>
      isActiveDaemonTriggerStatus(trigger.status) &&
      (trigger.targetDaemonId ?? trigger.claimedByDaemonId ?? '') === daemonId &&
      (excludeTriggerId === undefined || trigger.id !== excludeTriggerId) &&
      workspaceKeysOverlap(
        resolveWorkspaceKey({ ...trigger, checkoutId: resolveCheckoutId(trigger.agentConfigId) }),
        candidateWorkspaceKey,
      ) &&
      !(planMode && trigger.planMode),
  );

  if (conflictingTriggers.length === 0) {
    return { verdict: 'NONE', reason: null };
  }

  if (!conflictingTriggers.some((trigger) => runningDaemonTriggerStatuses.has(trigger.status))) {
    return { verdict: 'WARN', reason: 'QUEUED_BEHIND_PENDING' };
  }

  // 사유는 후보가 어느 계열의 작업공간을 쓰는지로 갈린다. 체크아웃이 확정된 요청도 메인 체크아웃이다.
  return {
    verdict: 'WARN',
    reason: isCheckoutFamilyKey(candidateWorkspaceKey) ? 'MAIN_CHECKOUT_BUSY' : 'SAME_WORKTREE_BUSY',
  };
}

/// 선택된 러너 기준 `agentConfigId → 체크아웃 폴더 id` 매핑을 만든다.
/// 다른 러너에서 관측된 체크아웃은 그 러너의 폴더이므로 여기서 제외한다 — 서버도 판정 대상 러너의
/// 매핑만 신뢰하고 나머지는 미보고로 본다. 미보고는 안전한 방향(경고)으로 접힌다.
export function buildCheckoutIdByAgentConfigId(agentConfigs, daemonId) {
  const map = new Map();
  if (daemonId.length === 0) {
    return map;
  }

  for (const config of agentConfigs) {
    if (config.runnerCheckout && config.runnerCheckout.daemonId === daemonId) {
      map.set(config.id, config.runnerCheckout.id);
    }
  }

  return map;
}

// WARN verdict일 때만 사유를 노출한다. WARN이 아니면 null.
export function activeConcurrencyWarningReasonFromVerdict(result) {
  return result.verdict === 'WARN' ? result.reason : null;
}

// ---------------------------------------------------------------------------
// 경고 문구 키 (원본: web/src/lib/daemonTriggerConflictMessages.ts, 그대로 이동)
// ---------------------------------------------------------------------------

// WARN 사유별 안내 문구 키(components.appLayout 네임스페이스). 생성 모달과 이어쓰기 화면이 공유한다.
export function activeConcurrencyWarningMessageKey(reason) {
  if (reason === 'MAIN_CHECKOUT_BUSY') return 'daemonTriggerActiveConcurrencyWarningMainCheckout';
  if (reason === 'SAME_WORKTREE_BUSY') return 'daemonTriggerActiveConcurrencyWarningSameWorktree';
  return 'daemonTriggerActiveConcurrencyWarningQueuedBehindPending';
}

// ---------------------------------------------------------------------------
// 프롬프트 내용 규칙 (원본: web/src/components/daemonTriggerDetailUtils.ts,
// web/src/hooks/useDaemonTriggerModalController.ts의 hasPromptContent)
// ---------------------------------------------------------------------------
// `hasPromptContent`(제출 가드: 텍스트 노드가 하나라도 있으면 통과)와
// `hasContent`(표시용: 공백 제외)가 다르다. 둘 다 기존 동작이므로 의미를 바꾸지 말고 유지한다.

export const getPlainText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'entityReference') return typeof node.attrs?.label === 'string' ? node.attrs.label : '';
  if (node.type === 'hardBreak') return '\n';
  if (node.content) {
    const separator = node.type === 'doc' ? '\n' : '';
    return node.content.map(getPlainText).join(separator);
  }
  return '';
};

export const getTextLength = (node) => {
  return getPlainText(node).length;
};

export const hasContent = (node) => {
  return getPlainText(node).trim().length > 0;
};

export const hasPromptContent = (node) => {
  if (node.type === 'text') {
    return (node.text ?? '').length > 0;
  }
  if (node.content) {
    return node.content.some(hasPromptContent);
  }
  return false;
};

export const appendPromptContent = (current, next) => {
  if (!hasContent(current)) {
    return next;
  }
  if (!hasContent(next)) {
    return current;
  }

  return {
    type: 'doc',
    content: [...(current?.content ?? []), ...(next.content ?? [])],
  };
};

export const removeEntityReference = (prompt, target) => {
  if (!prompt?.content) return prompt;

  let removed = false;
  const content = prompt.content
    .map((node) => {
      if (!node.content) return node;
      const nextContent = node.content.filter((child) => {
        const matches =
          child.type === 'entityReference' &&
          child.attrs?.entityType === target.entityType &&
          child.attrs?.entityId === target.entityId;
        if (matches) removed = true;
        return !matches;
      });
      if (nextContent.length === node.content.length) return node;
      // 엔티티 칩 뒤에는 캐럿용 공백 텍스트 노드가 붙어 있다. 칩을 제거한 뒤
      // 남은 자식이 공백뿐이면 문단을 비워, 아래 filter에서 함께 제거되도록 한다.
      const isWhitespaceOnly = nextContent.every((child) => child.type === 'text' && (child.text ?? '').trim() === '');
      return { ...node, content: isWhitespaceOnly ? [] : nextContent };
    })
    .filter((node) => node.type !== 'paragraph' || (node.content?.length ?? 0) > 0);

  return removed ? { ...prompt, content } : prompt;
};

export const hasEntityReference = (prompt, target) =>
  prompt?.content?.some((node) =>
    node.content?.some(
      (child) =>
        child.type === 'entityReference' &&
        child.attrs?.entityType === target.entityType &&
        child.attrs?.entityId === target.entityId,
    ),
  ) ?? false;

// ---------------------------------------------------------------------------
// 엔티티 참조 프리필 (원본: web/src/lib/entityReferencePrompt.ts)
// ---------------------------------------------------------------------------
// 활동 엔티티 8종(web/src/lib/api/me.ts의 MeFeedEntityType 미러)과 Tiptap 참조 타입의
// 매핑표다. 서버 피드 enum에 새 엔티티가 추가되면 원본의 Record 타입이 먼저 깨지므로
// 그때 이 표도 함께 갱신한다. 값 변경은 양쪽 앱의 참조 칩 렌더와 짝을 이룬다.

export const HOME_ACTIVITY_ENTITY_TYPES = Object.freeze([
  'PLAN',
  'COMPLETION_REPORT',
  'POST_MORTEM',
  'CO_ACTION',
  'CODE_REVIEW',
  'DOCUMENT',
  'CONVENTION',
  'SKILL',
]);

const tiptapEntityTypeByActivityEntityType = Object.freeze({
  PLAN: 'plan',
  COMPLETION_REPORT: 'completionReport',
  POST_MORTEM: 'postMortem',
  CO_ACTION: 'coAction',
  CODE_REVIEW: 'codeReview',
  DOCUMENT: 'document',
  CONVENTION: 'convention',
  SKILL: 'skill',
});

export const toTiptapEntityType = (entityType) => tiptapEntityTypeByActivityEntityType[entityType];

const nonEmpty = (value) => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : null;
};

export const resolveEntityReferenceLabel = ({ entityId, entityTypeLabel, labelCandidates = [] }) => {
  for (const candidate of labelCandidates) {
    const label = nonEmpty(candidate);
    if (label) return label;
  }

  return `${entityTypeLabel} ${entityId.slice(0, 8)}`;
};

export const buildEntityReferencePrefillPrompt = ({ entityType, entityId, label, path }) => ({
  type: 'doc',
  content: [
    {
      type: 'paragraph',
      content: [
        {
          type: 'entityReference',
          attrs: {
            entityType: toTiptapEntityType(entityType),
            entityId,
            label,
            ...(path ? { path } : {}),
          },
        },
        { type: 'text', text: ' ' },
      ],
    },
  ],
});

// ---------------------------------------------------------------------------
// 첨부 (원본: web/src/types/attachment.ts — 숫자 상수 제외)
// ---------------------------------------------------------------------------
// `maxAttachmentSizeBytes`·`maxAttachmentsPerDaemonTrigger` 같은 숫자 상수는 두지 않는다.
// 패키지 계약 검사(scripts/workspace-package-contract.test.mjs)가 숫자 리터럴 선언을
// 해석하지 못해 드리프트 대조에서 실패하므로, 숫자 제한은 소비 앱에 남긴다.

export const allowedAttachmentMimeTypes = Object.freeze([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/markdown',
  'application/pdf',
  'text/html',
]);

export const officeAttachmentMimeTypes = Object.freeze([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);

export const documentAttachmentMimeTypes = Object.freeze([...allowedAttachmentMimeTypes, ...officeAttachmentMimeTypes]);

export const isOfficeAttachmentMimeType = (mimeType) => officeAttachmentMimeTypes.includes(mimeType);

// MIME 정규화와 허용 목록 판정은 ui/types/attachment.ts 한 곳만 둔다.
// 루트에 같은 함수를 두면 텍스트 확장자 규칙이 조용히 갈라진다.

/**
 * 생성·이어하기 요청에 실을 첨부 payload로 바꾼다.
 * 서버에는 식별에 필요한 `key`·`originalName`만 전송한다(원본: 컨트롤러의 attachmentDrafts 매핑).
 */
export const toAttachmentDraftPayloads = (drafts) =>
  drafts.map((draft) => ({
    key: draft.key,
    originalName: draft.originalName,
  }));

// ---------------------------------------------------------------------------
// 이어하기 override (원본: web/src/components/daemonTriggerDetailUtils.ts의
// buildDaemonTriggerContinueOverrides — 상세 이어쓰기와 모달이 공유하는 순수 규칙)
// ---------------------------------------------------------------------------
// 모달 컨트롤러(useDaemonTriggerModalController)의 인라인 override 계산과는 planMode
// 취급이 다르다. 통일 여부는 어댑터 계약(Task 3)에서 결정하고, 여기서는 원본 그대로 둔다.

export const buildDaemonTriggerContinueOverrides = ({
  source,
  continueRunnerType,
  continueModel,
  continueFastMode,
  continueEffort,
  effectiveContinuePlanMode,
  continueUserConventionIds,
}) => {
  const overrides = {};
  if (continueRunnerType && continueRunnerType !== source?.runnerType) {
    overrides.runnerType = continueRunnerType;
    overrides.model = continueModel || null;
    overrides.fastMode = continueFastMode;
  } else if (continueModel !== (source?.model ?? '')) {
    overrides.model = continueModel || null;
    overrides.fastMode = continueFastMode;
  } else if (continueFastMode !== (source?.fastMode ?? false)) {
    overrides.fastMode = continueFastMode;
  }

  if (overrides.model !== undefined) {
    overrides.effort = continueEffort || null;
  } else if (continueEffort !== (source?.effort ?? '')) {
    overrides.effort = continueEffort || null;
  }
  if (effectiveContinuePlanMode !== (source?.planMode ?? false)) {
    overrides.planMode = effectiveContinuePlanMode;
  }
  const sourceUserConventionIds = source?.userConventionIds ?? [];
  const sameUserConventions =
    sourceUserConventionIds.length === continueUserConventionIds.length &&
    sourceUserConventionIds.every((id) => continueUserConventionIds.includes(id));
  if (!sameUserConventions) {
    overrides.userConventionIds = continueUserConventionIds;
  }
  return Object.keys(overrides).length > 0 ? overrides : undefined;
};

// ---------------------------------------------------------------------------
// 생성 payload (원본: web/src/hooks/useDaemonTriggerModalController.ts의
// handleSubmit 생성 분기 — 가드·빈 프롬프트 규칙·워크트리 조건·정책 블록)
// ---------------------------------------------------------------------------
// 성공 시 토스트·모달 닫기·선택값 저장은 앱 어댑터가 담당하고, 여기서는 전송 직전의
// payload 조립 규칙만 둔다. 가드 실패(원본의 조기 return)는 null로 돌려준다.

const emptyPromptDoc = () => ({
  type: 'doc',
  content: [{ type: 'paragraph' }],
});

export const isPolicyBasedCreateRequest = ({ mode, hasDispatchContext, selectedPolicyId }) =>
  mode === 'create' &&
  isRunnerDispatchPolicySelected({
    hasDispatchContext,
    selectedPolicyId,
  });

export const buildCreateTriggerPayload = (input) => {
  const {
    prompt,
    projectId,
    daemonId,
    agentConfigId,
    runnerType,
    model,
    fastMode,
    effort,
    planMode,
    isPlanModeForced,
    useWorktree,
    repositoryId,
    baseBranch,
    worktreeSelectionMode,
    selectedDiscoveredWorktreeId,
    selectedExistingWorktreeId,
    userConventionIds = [],
    dispatchContext,
    selectedPolicyId = null,
    attachmentDrafts = [],
    isProjectResourcesLoading = false,
    isAgentConfigCompatible = true,
    isResolvedSettingsError = false,
  } = input;

  if (
    !projectId ||
    isProjectResourcesLoading ||
    !daemonId ||
    !agentConfigId ||
    !runnerType ||
    !isAgentConfigCompatible ||
    isResolvedSettingsError
  ) {
    return null;
  }

  const daemonPrompt = prompt ?? emptyPromptDoc();
  const isPolicyBasedRequest = isPolicyBasedCreateRequest({
    mode: 'create',
    hasDispatchContext: dispatchContext !== undefined,
    selectedPolicyId,
  });
  if (!hasPromptContent(daemonPrompt) && !isPolicyBasedRequest) {
    return null;
  }

  return {
    prompt: daemonPrompt,
    agentConfigId,
    projectId: projectId || undefined,
    targetDaemonId: daemonId,
    runnerType,
    model: model || undefined,
    fastMode: fastMode || undefined,
    effort: effort || undefined,
    // 정책이 고른 기본값은 모달에서 이미 반영한다. false도 명시 전송해 사용자가
    // 정책 기본값을 해제한 선택이 서버의 기본 처리로 사라지지 않게 한다.
    planMode: isPlanModeForced || planMode,
    useWorktree: useWorktree || undefined,
    repositoryId: useWorktree ? repositoryId || undefined : undefined,
    baseBranch: useWorktree ? baseBranch || undefined : undefined,
    ...(useWorktree && worktreeSelectionMode === 'existing'
      ? selectedDiscoveredWorktreeId
        ? { discoveredWorktreeId: selectedDiscoveredWorktreeId }
        : selectedExistingWorktreeId
          ? { worktreeId: selectedExistingWorktreeId }
          : {}
      : {}),
    userConventionIds: userConventionIds.length > 0 ? userConventionIds : undefined,
    ...(isPolicyBasedRequest && dispatchContext && selectedPolicyId
      ? {
          dispatchPolicy: {
            policyId: selectedPolicyId,
            entityType: dispatchContext.entityType,
            entityId: dispatchContext.entityId,
            additionalPrompt: daemonPrompt,
          },
        }
      : {}),
    ...(attachmentDrafts.length > 0 ? { attachmentDrafts: toAttachmentDraftPayloads(attachmentDrafts) } : {}),
  };
};
